/**
 * SupportMetaDiffView
 *
 * Structured field-by-field diff for the typed metadata on a support
 * or FAQ page — summary, tags, modules, featured. Used inside
 * MergePreviewDialog and CommitDialog wherever a page's metadata
 * changed, alongside the body diff.
 *
 * Unchanged fields render as a quiet 'unchanged' row so the writer
 * sees the full picture; changed fields highlight via emerald (added)
 * / rose (removed) chips for arrays and inline highlights for the
 * summary string.
 */
import { useMemo } from "react";

export interface SupportMeta {
  summary?: string;
  tags?: string[];
  modules?: string[];
  featured?: boolean;
}

interface Props {
  before: SupportMeta | null | undefined;
  after: SupportMeta | null | undefined;
  beforeLabel?: string;
  afterLabel?: string;
}

function normalize(m: SupportMeta | null | undefined): SupportMeta {
  if (!m) return {};
  return {
    summary: m.summary || "",
    tags: m.tags || [],
    modules: m.modules || [],
    featured: m.featured ?? false,
  };
}

/** Set-diff of two string arrays — returns added/removed sets. */
function arrayDiff(a: string[], b: string[]) {
  const aSet = new Set(a);
  const bSet = new Set(b);
  const removed = a.filter((x) => !bSet.has(x));
  const added = b.filter((x) => !aSet.has(x));
  const same = a.filter((x) => bSet.has(x));
  return { added, removed, same };
}

function FieldHeader({ label, changed }: { label: string; changed: boolean }) {
  return (
    <div className="flex items-center gap-2 mb-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {changed && (
        <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-px rounded">
          changed
        </span>
      )}
    </div>
  );
}

function TagRow({
  added, removed, unchanged,
}: { added: string[]; removed: string[]; unchanged: string[] }) {
  if (added.length === 0 && removed.length === 0 && unchanged.length === 0) {
    return <span className="text-[12px] text-slate-400 italic">none</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {removed.map((t) => (
        <span
          key={`r-${t}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-rose-50 text-rose-700 border border-rose-200 line-through"
        >
          {t}
        </span>
      ))}
      {added.map((t) => (
        <span
          key={`a-${t}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium"
        >
          {t}
        </span>
      ))}
      {unchanged.map((t) => (
        <span
          key={`u-${t}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-slate-50 text-slate-600 border border-slate-200"
        >
          {t}
        </span>
      ))}
    </div>
  );
}

function SummaryRow({ before, after }: { before: string; after: string }) {
  const changed = before !== after;
  if (!changed) {
    return (
      <p className="text-[13px] leading-relaxed text-slate-600 italic">
        {before || <span className="text-slate-400">empty</span>}
      </p>
    );
  }
  return (
    <div className="space-y-1">
      {before && (
        <div className="flex gap-1.5">
          <span className="shrink-0 text-rose-500 font-bold">−</span>
          <p className="text-[13px] leading-relaxed text-rose-700 bg-rose-50/60 px-2 py-1 rounded flex-1 line-through decoration-rose-300">
            {before}
          </p>
        </div>
      )}
      {after && (
        <div className="flex gap-1.5">
          <span className="shrink-0 text-emerald-500 font-bold">+</span>
          <p className="text-[13px] leading-relaxed text-emerald-700 bg-emerald-50/60 px-2 py-1 rounded flex-1">
            {after}
          </p>
        </div>
      )}
      {!before && !after && (
        <span className="text-[12px] text-slate-400 italic">empty</span>
      )}
    </div>
  );
}

function ToggleRow({ before, after }: { before: boolean; after: boolean }) {
  const changed = before !== after;
  if (!changed) {
    return (
      <span
        className={
          "text-[12px] " +
          (before ? "text-slate-700 font-medium" : "text-slate-400 italic")
        }
      >
        {before ? "on" : "off"}
      </span>
    );
  }
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 line-through">
        {before ? "on" : "off"}
      </span>
      <span className="text-slate-300">→</span>
      <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
        {after ? "on" : "off"}
      </span>
    </div>
  );
}

export default function SupportMetaDiffView({
  before, after, beforeLabel, afterLabel,
}: Props) {
  const b = useMemo(() => normalize(before), [before]);
  const a = useMemo(() => normalize(after), [after]);

  const summaryChanged = (b.summary || "") !== (a.summary || "");
  const tagDiff = arrayDiff(b.tags || [], a.tags || []);
  const moduleDiff = arrayDiff(b.modules || [], a.modules || []);
  const tagsChanged = tagDiff.added.length > 0 || tagDiff.removed.length > 0;
  const modulesChanged = moduleDiff.added.length > 0 || moduleDiff.removed.length > 0;
  const featuredChanged = (b.featured || false) !== (a.featured || false);

  const anyChanged = summaryChanged || tagsChanged || modulesChanged || featuredChanged;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Article metadata
        </div>
        {(beforeLabel || afterLabel) && (
          <div className="text-[10px] text-slate-400 flex items-center gap-1.5">
            {beforeLabel && <span>{beforeLabel}</span>}
            {beforeLabel && afterLabel && <span>→</span>}
            {afterLabel && <span>{afterLabel}</span>}
          </div>
        )}
      </div>

      {!anyChanged && (
        <div className="text-[12px] text-slate-400 italic">
          No metadata changes.
        </div>
      )}

      <div>
        <FieldHeader label="Summary" changed={summaryChanged} />
        <SummaryRow before={b.summary || ""} after={a.summary || ""} />
      </div>

      <div>
        <FieldHeader label="Tags" changed={tagsChanged} />
        <TagRow {...tagDiff} unchanged={tagDiff.same} />
      </div>

      <div>
        <FieldHeader label="Modules" changed={modulesChanged} />
        <TagRow {...moduleDiff} unchanged={moduleDiff.same} />
      </div>

      <div>
        <FieldHeader label="Featured" changed={featuredChanged} />
        <ToggleRow before={b.featured || false} after={a.featured || false} />
      </div>
    </div>
  );
}
